import React from "react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { IconMenu, IconGridLarge, IconGridSmall } from "icons";
import { SleekSegmented } from "components/Sleek/SleekToggle";
import { SLEEK_CONTROL } from "pages/DesignCanvasAgentPage/components/sleek";

export type AssetViewMode = "grid" | "list";

export interface ViewControlsProps {
  viewMode: AssetViewMode;
  setViewMode: (mode: AssetViewMode) => void;
  columns: number;
  setColumns: (n: number) => void;
  minColumns?: number;
  maxColumns?: number;
  // Hide the grid/list toggle (density slider only) for surfaces that are grid-only.
  gridOnly?: boolean;
}

// Grid/list view controls for the asset filter bars, in the Trends pages' finish (2026-10-26). In grid mode it also exposes a column-density
// slider (hidden in list mode). `gridOnly` drops the grid/list toggle entirely.
const ViewControls = ({ viewMode, setViewMode, columns, setColumns, minColumns = 3, maxColumns = 8, gridOnly = false }: ViewControlsProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex items-center gap-2">
      {viewMode === "grid" && (
        // The slider is inverted from the raw column count: dragging toward the large-grid icon
        // lowers the column count so cards grow bigger. value/onChange mirror around min+max.
        <div className={clsx(SLEEK_CONTROL, "flex h-9 items-center gap-2 rounded-full px-3")}>
          <IconGridSmall className="w-4 h-4 flex-shrink-0 text-grey" />
          <input
            type="range"
            min={minColumns}
            max={maxColumns}
            value={minColumns + maxColumns - columns}
            onChange={(e) => setColumns(minColumns + maxColumns - Number(e.target.value))}
            aria-label={t("assets:_shared.view.columns")}
            className="w-20 h-1 cursor-pointer accent-purpleGreen"
          />
          <IconGridLarge className="w-4 h-4 flex-shrink-0 text-grey" />
        </div>
      )}

      {/* Grid / List — one pill of icons in the Trends pages' finish (2026-10-26) */}
      {!gridOnly && (
        <SleekSegmented
          value={viewMode}
          onChange={setViewMode}
          options={[
            { id: "grid", icon: <IconGridSmall className="w-4 h-4" />, label: t("assets:_shared.view.grid_view") },
            { id: "list", icon: <IconMenu className="w-4 h-4" />, label: t("assets:_shared.view.list_view") },
          ]}
        />
      )}
    </div>
  );
};

export default ViewControls;
